/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import './scopeDataGrid.scss';
import { Checkbox, IconButton } from '@mui/material';
import UnfoldMoreSharpIcon from '@mui/icons-material/UnfoldMoreSharp';
import KeyboardArrowDownSharpIcon from '@mui/icons-material/KeyboardArrowDownSharp';
import KeyboardArrowUpSharpIcon from '@mui/icons-material/KeyboardArrowUpSharp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MagnifyingGlass from '../../../../Assets/images/svg/dar_magnifying.svg';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PaginationButtons from '../../../../Components/DataGrid/pagination';
import ClearIcon from '@mui/icons-material/Clear';
import Loader from '../../../../Components/Loader';

const DataGridComponent = ({
  dataRows,
  dataColumns,
  pageSize,
  isFilteredRows,
  sortFilterColumns,
  isEdit,
  gridCheckbox = false,
  onRowSelectionChange,
  dataGridCss,
  checkboxDisabled,
  rowHeight = 52,
  noDataImage = null,
  noDataTitle = 'No Epic/Stories Have Been Created Yet',
  noDataSubTitle = 'Please add an Epic/Story to view here',
  noDataDivColor = 'white',
  hideSeparator = false,
  editStateHeight = 180,
  getRowHeight,
  handleRowEnter,
  handleRowLeave,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortModel, setSortModel] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('');
  const [filteredSortedData, setFilteredSortedData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [checkClick, setCheckClick] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [selectedRow, setSelectedRow] = useState([]);
  const [loading, setLoading] = useState(true);

  const defaultGetRowHeight = (params) => {
    const row = paginatedData.find((r) => r.id === params.id);
    return row?.isEditing ? editStateHeight : 52;
  };

  useEffect(() => {
    setCurrentPage(currentPage);
  }, [searchText, sortModel, isFilteredRows]);

  useEffect(() => {
    setCurrentPage(Number(currentPage));
  }, [isEdit]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const filteredData = filterData(dataRows, searchText, searchColumn);
      const sortedData = sortData(filteredData, sortModel);
      setFilteredSortedData(sortedData);
      setLoading(false);
    };
    fetchData();
  }, [dataRows, searchText, sortModel, isFilteredRows]);

  useEffect(() => {
    let paginated = filteredSortedData.slice(
      (currentPage - 1) * pageSize,
      (currentPage - 1) * pageSize + pageSize
    );
    setPaginatedData(paginated);
  }, [currentPage, filteredSortedData, pageSize]);

  useEffect(() => {
    if (
      paginatedData.length < pageSize &&
      currentPage > 1 &&
      filteredSortedData.length < (currentPage - 1) * pageSize + 1
    ) {
      setCurrentPage(currentPage - 1);
    }
  }, [paginatedData, pageSize, currentPage, filteredSortedData.length]);

  const NoDataOverlay = ({ imageSrc }) => (
    <div
      className="no-data-overlay"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        backgroundColor: noDataDivColor,
      }}
    >
      {imageSrc ? (
        <>
          <img
            src={imageSrc}
            alt="No Data Found"
            style={{ maxWidth: '100%', marginBottom: '10px' }}
          />
          <div>
            <p>{noDataTitle}</p>
            <div>{noDataSubTitle}</div>
          </div>
        </>
      ) : (
        <p>No records found</p>
      )}
    </div>
  );

  NoDataOverlay.propTypes = {
    imageSrc: PropTypes.any,
  };
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const getRowClassName = (params) => {
    return params.indexRelativeToCurrentPage % 2 === 0 ? 'odd-row' : 'even-row';
  };

  const filterData = (dataRows, searchText, searchColumn) => {
    return searchText.length >= 1
      ? dataRows.filter((row) => {
          const columnValue = row[searchColumn];
          if (typeof columnValue === 'number') {
            return columnValue.toString().includes(searchText);
          } else if (typeof columnValue === 'string') {
            return columnValue.toLowerCase().includes(searchText.toLowerCase());
          } else if (typeof columnValue === 'boolean') {
            return columnValue.toString().includes(searchText);
          }
          return false;
        })
      : dataRows;
  };

  const compareBooleans = (aValue, bValue, sortOrder) => {
    const aBoolValue = aValue ? 1 : 0;
    const bBoolValue = bValue ? 1 : 0;
    return sortOrder === 'asc'
      ? aBoolValue - bBoolValue
      : bBoolValue - aBoolValue;
  };

  const compareNumbers = (aValue, bValue, sortOrder) => {
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  };

  const compareStrings = (aValue, bValue, sortOrder) => {
    const aStrValue = aValue ? aValue.toString().toLowerCase() : '';
    const bStrValue = bValue ? bValue.toString().toLowerCase() : '';
    if (aStrValue < bStrValue) return sortOrder === 'asc' ? -1 : 1;
    if (aStrValue > bStrValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  };

  const sortData = (data, sortModel) => {
    if (sortModel.length === 0) return data;

    const { field: sortField, sort: sortOrder } = sortModel[0];

    return data.slice().sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return compareBooleans(aValue, bValue, sortOrder);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return compareNumbers(aValue, bValue, sortOrder);
      }

      return compareStrings(aValue, bValue, sortOrder);
    });
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
  };

  const handleMouseEnter = (field) => {
    !searchText && setSearchColumn(field);
  };
  const handleMouseLeave = () => {
    if (!searchText) {
      setAnchorEl(null);
      setSearchColumn('');
    }
  };

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleSearch = (event, field) => {
    const value = event.target.value.toLowerCase();

    if (value === '') {
      setAnchorEl(null);
      setCheckClick(false);
      setSearchColumn('');
    }
    setSearchText(value);
  };

  const getSortingIcon = (field) => {
    const sortItem = sortModel.find((item) => item.field === field) || {};
    let icon;

    switch (sortItem.sort) {
      case 'asc':
        icon = <KeyboardArrowUpSharpIcon />;
        break;
      case 'desc':
        icon = <KeyboardArrowDownSharpIcon />;
        break;
      default:
        icon = <UnfoldMoreSharpIcon />;
        break;
    }

    return icon;
  };

  const getFilterIcon = (field) => {
    const handleClickAway = () => {
      if (searchText) {
        setCheckClick(true);
      }
      if (cleared && !searchText) {
        setSearchColumn('');
      }
      setAnchorEl(null);
    };

    return (
      field == searchColumn && (
        <>
          {checkClick && searchText ? (
            <FilterAltIcon onClick={handleClick} className="fliter-alt-icon" />
          ) : (
            <MoreVertIcon onClick={handleClick} className="more-vert-icon" />
          )}
          <ClickAwayListener onClickAway={handleClickAway}>
            <Popper
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              placement="bottom-start"
            >
              <Paper className="popper-paper">
                <div className="popper-text-filter-text">Text Filter</div>
                <div className="popper-text-filter-img">
                  <img src={MagnifyingGlass} alt="Search" />
                  <input
                    id="datagrid-search-input"
                    type="text"
                    placeholder="Search..."
                    value={searchText}
                    onChange={(event) => handleSearch(event, field)}
                  />
                  {searchText && (
                    <IconButton
                      onClick={() => {
                        setSearchText('');
                        setCleared(true);
                        setAnchorEl(null);
                        setCheckClick(false);
                        setSearchColumn('');
                      }}
                      className="clear-search-icon"
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </div>
              </Paper>
            </Popper>
          </ClickAwayListener>
        </>
      )
    );
  };

  const determineNewSortModel = (columnField) => {
    const isBooleanColumn = dataRows.some(
      (row) => typeof row[columnField] === 'boolean'
    );

    if (isBooleanColumn) {
      if (sortModel[0]?.field === columnField) {
        if (sortModel[0]?.sort === 'desc') {
          return [{ field: columnField, sort: 'asc' }];
        } else if (sortModel[0]?.sort === 'asc') {
          return [];
        }
      }
      return [{ field: columnField, sort: 'desc' }];
    } else {
      if (sortModel[0]?.field === columnField) {
        if (sortModel[0]?.sort === 'asc') {
          return [{ field: columnField, sort: 'desc' }];
        } else if (sortModel[0]?.sort === 'desc') {
          return [];
        }
      }
      return [{ field: columnField, sort: 'asc' }];
    }
  };

  const enhancedColumns = dataColumns.map((column) => {
    if (sortFilterColumns.includes(column.field)) {
      return {
        ...column,
        renderHeader: (params) => {
          const { colDef } = params;
          const isSorted =
            sortModel.length > 0 && sortModel[0].field === colDef.field;
          return (
            <button
              className={`header-column-sort-filter ${
                isSorted || searchText ? 'header-column-sorted' : ''
              }`}
              onMouseEnter={() => handleMouseEnter(column.field)}
              onMouseLeave={handleMouseLeave}
            >
              <Tooltip title={params.colDef.headerName} arrow>
                {params.colDef.headerName}
              </Tooltip>
              <IconButton
                onClick={() =>
                  handleSortModelChange(determineNewSortModel(column.field))
                }
              >
                {getSortingIcon(column.field)}
              </IconButton>
              {!params.colDef.hideCoumnMenuIcon && getFilterIcon(column.field)}
            </button>
          );
        },
      };
    } else {
      return column;
    }
  });

  if (gridCheckbox) {
    // Add custom checkbox column
    const checkboxColumn = {
      field: 'checkbox',
      headerName: '',
      width: 70,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Checkbox
          checked={
            params.row.roleName !== 'NA' || selectedRow.includes(params.id)
          }
          disabled={params.row.roleName !== 'NA' || checkboxDisabled}
          onChange={() => handleCheckboxChange(params.id)}
          inputProps={{ 'aria-label': `Checkbox ${params.id}` }}
        />
      ),
    };

    // Insert checkbox column at the beginning of the columns array
    enhancedColumns.unshift(checkboxColumn);

    const handleCheckboxChange = (id) => {
      const newSelection = selectedRow.includes(id)
        ? selectedRow.filter((selectedId) => selectedId !== id)
        : [...selectedRow, id];
      setSelectedRow(newSelection);
      onRowSelectionChange(newSelection);
    };
  }

  const paginationButton = () => {
    return (
      <PaginationButtons
        currentPage={currentPage}
        pageSize={pageSize}
        paginatedData={paginatedData}
        filteredSortedData={filteredSortedData}
        handlePageChange={handlePageChange}
      />
    );
  };

  const renderNoDataOverlay = (noDataImage) => {
    return <NoDataOverlay imageSrc={noDataImage} />;
  };
  return (
    <div className={`data-grid ${dataGridCss}`}>
      {loading ? (
        <Loader />
      ) : (
        <DataGrid
          autoHeight //NOSONAR
          disableColumnSelector
          rows={paginatedData}
          columns={enhancedColumns}
          disableRowSelectionOnClick
          getRowClassName={getRowClassName}
          getRowHeight={getRowHeight || defaultGetRowHeight}
          rowHeight={rowHeight}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: pageSize },
            },
          }}
          paginationMode="client"
          slots={{
            pagination: paginationButton,
            noRowsOverlay: () => renderNoDataOverlay(noDataImage),
          }}
          slotProps={{
            row: {
              onMouseEnter: handleRowEnter,
              onMouseLeave: handleRowLeave,
            },
          }}
          disableDensitySelector
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          localeText={{ noRowsLabel: 'No records found' }}
          columnBuffer={paginatedData.length}
          sx={
            hideSeparator === true
              ? {
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

DataGridComponent.propTypes = {
  dataRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  dataColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageSize: PropTypes.number.isRequired,
  isFilteredRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  sortFilterColumns: PropTypes.array.isRequired,
  isEdit: PropTypes.bool,
  gridCheckbox: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
  dataGridCss: PropTypes.any,
  checkboxDisabled: PropTypes.bool,
  rowHeight: PropTypes.number,
  noDataImage: PropTypes.any,
  noDataTitle: PropTypes.string,
  noDataSubTitle: PropTypes.string,
  noDataDivColor: PropTypes.string,
  hideSeparator: PropTypes.bool,
  editStateHeight: PropTypes.number,
  getRowHeight: PropTypes.func,
  handleRowEnter: PropTypes.func,
  handleRowLeave: PropTypes.func,
};

export default DataGridComponent;
